import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import TurnstileWidget from '../../components/TurnstileWidget';
import { TURNSTILE_SITE_KEY, API_CONFIG } from '../../config';

/**
 * Public (unauthenticated) "Request early access" form for the invite-only
 * preview (ADR-0044, #238). Colocated with the portal's other public auth pages
 * so it reuses the SAME Cloudflare Turnstile widget + site key as registration.
 * POSTs to the unauthenticated developer-API route POST /developer/request-access,
 * which writes a PENDING AccessRequest and notifies ops by id (never PHI).
 */
export default function RequestAccessPage() {
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Anti-bot challenge token (ADR-0021) — same widget/site key as RegisterPage.
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRequired = Boolean(TURNSTILE_SITE_KEY);
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  // Channel attribution (#340) — capture UTM params + referrer from the landing
  // URL once on mount (before any in-app navigation can drop the query string).
  // These are non-PII campaign labels persisted server-side WITH the request so a
  // signup can be attributed to its source channel; nothing is sent to an
  // analytics tool. Best-effort: any failure here must not affect the form.
  const [attribution, setAttribution] = useState({});
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const a = {};
      const src = params.get('utm_source');
      const med = params.get('utm_medium');
      const camp = params.get('utm_campaign');
      if (src) a.utmSource = src;
      if (med) a.utmMedium = med;
      if (camp) a.utmCampaign = camp;
      // The external page that linked here (empty string for direct visits).
      if (document.referrer) a.referrer = document.referrer;
      setAttribution(a);
    } catch (_e) {
      // attribution is best-effort — ignore
    }
  }, []);

  const onSubmit = async ({ email, company, useCase }) => {
    setError('');
    if (captchaRequired && !captchaToken) {
      setError('Please complete the "I\'m human" check before continuing.');
      return;
    }
    try {
      const resp = await fetch(`${API_CONFIG.developerApiBase}/developer/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company, useCase, captchaToken, ...attribution }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.message || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
    } catch (e) {
      // A consumed/expired token can't be reused — clear it so the widget reissues.
      setCaptchaToken('');
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  if (submitted) {
    return (
      <AuthCard title="Thanks — we've got it" subtitle="Vectros is in invite-only preview" align="center">
        <Typography variant="body2" color="text.secondary" textAlign="center">
          We review every request and open access in waves. There&apos;s nothing more to do
          for now — if it&apos;s a fit, we&apos;ll email you (at the address you gave) when your
          access is ready, with the steps to get started. It&apos;s worth checking your spam
          folder so our note doesn&apos;t get missed.
        </Typography>
        <Typography variant="body2" textAlign="center" mt={3} color="text.secondary">
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#3B82F6', fontWeight: 500 }}>Sign in</Link>
        </Typography>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Request early access" subtitle="Vectros is in invite-only preview">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Work email" type="email" size="small" fullWidth
          {...register('email', { required: true })} />
        <TextField label="Company (optional)" size="small" fullWidth
          inputProps={{ maxLength: 200 }}
          {...register('company')} />
        <TextField label="What are you building? (optional)" size="small" fullWidth
          multiline minRows={3}
          inputProps={{ maxLength: 2000 }}
          // General-purpose platform: warn off regulated data on this pre-BAA form
          // without framing Vectros as health-only.
          helperText="This intake isn't covered by a BAA — please don't include sensitive or regulated data (e.g. PHI)."
          {...register('useCase')} />
        {captchaRequired && (
          <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
        )}
        <Button type="submit" variant="contained" fullWidth
          disabled={isSubmitting || (captchaRequired && !captchaToken)}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          {isSubmitting ? 'Sending…' : 'Request early access'}
        </Button>
      </Box>
      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        Already have an account?{' '}
        <Link to="/login" style={{ color: '#3B82F6', fontWeight: 500 }}>Sign in</Link>
      </Typography>
    </AuthCard>
  );
}
